import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, ShieldCheckIcon, FileTextIcon } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
const privacyContent = {
  title: 'Política de Privacidad',
  icon: ShieldCheckIcon,
  sections: [{
    heading: 'Información que Recopilamos',
    text: 'Recopilamos únicamente la información necesaria para proporcionarte el plan antiinflamatorio de 14 días. Esto puede incluir tu dirección de correo electrónico al momento de la compra.'
  }, {
    heading: 'Uso de la Información',
    text: 'Tu información se utiliza exclusivamente para procesar tu compra y enviarte el plan. No compartimos, vendemos ni distribuimos tu información personal a terceros.'
  }, {
    heading: 'Protección de Datos',
    text: 'Implementamos medidas de seguridad estándar de la industria para proteger tu información personal contra acceso no autorizado, alteración o destrucción.'
  }, {
    heading: 'Cookies',
    text: 'Nuestro sitio puede utilizar cookies para mejorar tu experiencia de navegación. Puedes configurar tu navegador para rechazar cookies si lo prefieres.'
  }, {
    heading: 'Tus Derechos',
    text: 'Tienes derecho a acceder, corregir o eliminar tu información personal en cualquier momento. Para ejercer estos derechos, puedes contactarnos directamente.'
  }]
};
const termsContent = {
  title: 'Términos y Condiciones',
  icon: FileTextIcon,
  sections: [{
    heading: 'Aceptación de los Términos',
    text: 'Al adquirir y utilizar el Plan Antiinflamatorio de 14 Días, aceptas estos términos y condiciones en su totalidad.'
  }, {
    heading: 'Naturaleza del Producto',
    text: 'Este plan es un producto digital informativo y educativo. No constituye consejo médico profesional. Consulta siempre a un profesional de la salud antes de iniciar cualquier cambio en tu alimentación.'
  }, {
    heading: 'Uso Personal',
    text: 'El plan es para uso personal e intransferible. Queda prohibida su reproducción, distribución o reventa sin autorización expresa.'
  }, {
    heading: 'Política de Reembolso',
    text: 'Debido a la naturaleza digital del producto, todas las ventas son finales. Si tienes algún problema con tu compra, contáctanos y haremos lo posible por ayudarte.'
  }, {
    heading: 'Limitación de Responsabilidad',
    text: 'Los resultados pueden variar de persona a persona. No garantizamos resultados específicos. El uso del plan es bajo tu propia responsabilidad y criterio.'
  }, {
    heading: 'Modificaciones',
    text: 'Nos reservamos el derecho de modificar estos términos en cualquier momento. Los cambios serán efectivos inmediatamente después de su publicación.'
  }]
};
function Modal({
  type,
  onClose
}: {
  type: 'privacy' | 'terms';
  onClose: () => void;
}) {
  const content = type === 'privacy' ? privacyContent : termsContent;
  const Icon = content.icon;
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.2
  }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose} data-id="element-28">
      <motion.div initial={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} transition={{
      duration: 0.3,
      ease: 'easeOut'
    }} className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-29">
        {/* Header */}
        <div className="flex items-center justify-between p-6 sm:p-8 border-b border-wellness-sage/20" data-id="element-30">
          <div className="flex items-center gap-3" data-id="element-31">
            <div className="p-2 rounded-xl bg-wellness-sage/20 text-wellness-primary" data-id="element-32">
              <Icon className="w-5 h-5" data-id="element-33" />
            </div>
            <h2 className="text-2xl font-bold text-wellness-primary font-serif" data-id="element-34">
              {content.title}
            </h2>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-wellness-sage/20 text-wellness-text/60 hover:text-wellness-primary transition-colors" data-id="element-35">
            <XIcon className="w-5 h-5" data-id="element-36" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 sm:p-8 overflow-y-auto max-h-[calc(85vh-88px)] space-y-6" data-id="element-37">
          {content.sections.map((section, idx) => <div key={idx} data-id="element-38">
              <h3 className="text-lg font-semibold text-wellness-primary mb-2" data-id="element-39">
                {section.heading}
              </h3>
              <p className="text-wellness-text/80 leading-relaxed" data-id="element-40">
                {section.text}
              </p>
            </div>)}
          <p className="text-sm text-wellness-text/50 pt-4 border-t border-wellness-sage/20" data-id="element-41">
            Última actualización:{' '}
            {new Date().toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })}
          </p>
        </div>
      </motion.div>
    </motion.div>;
}
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="bg-wellness-primary py-8 px-4 sm:px-6" data-id="element-42">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-43">
          <p className="text-wellness-sage/60 text-sm" data-id="element-44">
            © {new Date().getFullYear()} Plan Antiinflamatorio. Consulta a un
            profesional de la salud antes de iniciar cualquier dieta.
          </p>
          <div className="flex items-center gap-6" data-id="element-45">
            <button onClick={() => setActiveModal('privacy')} className="text-wellness-sage/60 hover:text-wellness-sage text-sm transition-colors" data-id="element-46">
              Política de Privacidad
            </button>
            <span className="text-wellness-sage/30" data-id="element-47">·</span>
            <button onClick={() => setActiveModal('terms')} className="text-wellness-sage/60 hover:text-wellness-sage text-sm transition-colors" data-id="element-48">
              Términos y Condiciones
            </button>
          </div>
        </div>
      </footer>

      <AnimatePresence data-id="element-49">
        {activeModal && <Modal type={activeModal} onClose={() => setActiveModal(null)} data-id="element-50" />}
      </AnimatePresence>
    </>;
}